/**
 * Discovery / SQL-object usage - "which views, procedures, functions and SQL
 * Agent job steps touch this column".
 *
 * Sources: GET /v1/discovery/sql-objects/references (discovery:read), and
 * GET /v1/discovery/sql-objects/lineage (discovery:read) for the "Personal
 * data lineage" section (Bank: database-object lineage).
 *
 * The capability existed behind the API with no way to ask from the product.
 * This page is the asking surface, and its whole job is to carry the THREE
 * numbers the answer is made of without flattening them into one:
 *
 *   `references` is the page, `total` is the genuine count behind it, and
 *   `unparsedObjects` is how many objects were in scope and could NOT be read.
 *
 * That third number is why this page is shaped the way it is. A list of
 * references on its own says "these are the objects that touch pan_number";
 * the truthful statement is "these are the ones we could read, and N more we
 * could not". So `unparsedObjects` is rendered ALWAYS - including when it is
 * zero, where it is stated out loud rather than omitted, because an absent
 * statement reads as a clean bill of health nobody actually issued.
 *
 * The same rule governs the row: an unresolved reference is shown (it is real
 * information) but never dressed as a confirmed location, and a `referenced`
 * row never claims data moved.
 */

import { useCallback, useState } from 'react';
import { motion } from 'motion/react';
import {
  Search,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
  ArrowRight,
  Database,
  Server,
  FileCode,
  Workflow,
  ChevronLeft,
  ChevronRight,
  ListTree,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import {
  getSqlObjectLineage,
  getSqlObjectReferences,
  SQL_OBJECT_DATABASE_UNSET,
  type SqlObjectLineageEdge,
  type SqlObjectLineageResponse,
  type SqlObjectLineageTable,
  type SqlObjectPartialCoverageSource,
  type SqlObjectReference,
  type SqlObjectReferencesResponse,
  type SqlObjectUnexaminedReason,
} from '@/lib/api';

/** One page of references. The API caps `limit` at 500; 100 is its default. */
const PAGE_SIZE = 100;

/**
 * The five statuses the controller's `SQL_OBJECT_STATUSES` allows, in plain
 * words. NOT collapsed into "ok / not ok": `partially_parsed` contributed some
 * references and lost others, which is a different fact from a body that would
 * not parse at all, and a reader acts differently on each.
 */
const STATUS_COPY = {
  ok: 'Parsed cleanly',
  parse_failed: 'Body would not parse',
  partially_parsed: 'Partially parsed - some references were lost',
  unsupported_dialect: 'Dialect not supported',
  sidecar_absent: 'Parser unavailable when this object was read',
  not_analysed: 'Not analysed - past a scan limit (object count, body size or time)',
} as const satisfies Record<string, string>;

/** The statuses that mean this object's column list is not trustworthy. */
const UNTRUSTWORTHY_STATUSES = [
  'parse_failed',
  'partially_parsed',
  'unsupported_dialect',
  'sidecar_absent',
  'not_analysed',
] as const;

/**
 * The six object kinds. `job_step` is a SQL Agent batch job step and is the
 * one most likely to surprise a reader - a scheduled batch touching a
 * sensitive column is not what people picture when they think "SQL object" -
 * so the kind is on every row rather than assumed from context.
 */
const KIND_COPY = {
  view: 'View',
  procedure: 'Procedure',
  function: 'Function',
  package_body: 'Package body',
  job_step: 'SQL Agent job step',
  materialized_view: 'Materialized view',
  trigger: 'Trigger',
  java_class: 'Java class',
} as const satisfies Record<string, string>;

/**
 * Label lookups fall back to the RAW value rather than to a friendly guess.
 * If the API ever grows a sixth kind or status, this page must show the
 * unfamiliar token and let the reader notice it - substituting a nearby known
 * value would be inventing one.
 */
function kindLabel(kind: string): string {
  return kind in KIND_COPY ? KIND_COPY[kind as keyof typeof KIND_COPY] : kind;
}

function statusLabel(status: string): string {
  return status in STATUS_COPY ? STATUS_COPY[status as keyof typeof STATUS_COPY] : status;
}

function isUntrustworthy(status: string): boolean {
  return (UNTRUSTWORTHY_STATUSES as ReadonlyArray<string>).includes(status);
}

/** The explicit rendering of the no-database sentinel. Never blank, never a dash. */
export const DATABASE_NOT_APPLICABLE = 'database not applicable';

/**
 * What a missing identity segment renders as. Never '' and never omitted: a path
 * with a segment silently dropped reads as a shorter path that is complete.
 */
export const SEGMENT_NOT_REPORTED = '(not reported)';

/**
 * The four-field source locator, rendered whole.
 *
 * `sourceEngine` + `sourceHost` + `sourcePort` + `sourceDatabase` together are
 * the location identity (docs/superpowers/specs/2026-09-08-data-location-identity.md).
 * A schema-qualified object name without its database and server is ONE SEGMENT
 * of an identity, not an identity - two servers can each have a `dbo.vw_customer`,
 * and an answer that renders only the name invites the reader to act on the
 * wrong one. So all four are always shown together and none is ever dropped
 * because it looked redundant.
 */
export function formatSourceLocator(ref: SqlObjectReference): string {
  const database =
    ref.sourceDatabase === SQL_OBJECT_DATABASE_UNSET
      ? DATABASE_NOT_APPLICABLE
      : ref.sourceDatabase;
  return `${ref.sourceEngine}://${ref.sourceHost}:${ref.sourcePort}/${database}`;
}

/**
 * Where the column this row is about actually sits.
 *
 * Only stated as a path when `resolved` is true. An unresolved row knows the
 * column NAME and nothing reliable about its enclosing table, so printing
 * `schema.table.column` for it would manufacture a location the row does not
 * carry.
 */
export function formatColumnSite(ref: SqlObjectReference): string {
  if (!ref.resolved) return ref.columnName;
  // An absent segment gets the EXPLICIT sentinel, never omission. `.filter(Boolean)`
  // dropped a null schema and rendered `customers.pan_number`, which reads as a
  // complete two-segment path rather than as a path with a segment missing - the
  // collapse the identity spec forbids by name ("not an empty string, and not by
  // omitting the segment"). Unreachable from the real producer, which requires
  // both schema and table before `resolved` is true; guarded anyway, because the
  // same invariant is guarded loudly one function later for a `derived` row with
  // no target and two treatments of one rule in one file is how the weaker one
  // survives.
  const segment = (v: string | null) => (v === null || v === '' ? SEGMENT_NOT_REPORTED : v);
  return [segment(ref.columnSchema), segment(ref.columnTable), ref.columnName].join('.');
}

/**
 * What an answer was narrowed to, in words a claim can be made about.
 *
 * Low finding 12: the notice was given only the COLUMN, so with `table=customers`
 * active it still said "the complete set of objects this platform can see that
 * touch pan_number". The true statement is about `customers.pan_number`. A claim
 * of completeness has to name the set it is complete over.
 *
 * NOT built by joining the filters that happen to be set - an unset filter is
 * "not narrowed", which is a different thing from an absent segment of an
 * identity, and spelling it as a dotted path would invent a location. Each
 * combination is written out.
 */
export function describeScope(filters: SqlObjectFilters): string {
  const { column, table, schema } = filters;
  if (table && schema) return `${schema}.${table}.${column}`;
  if (table) return `${table}.${column}`;
  if (schema) return `${column} in schema ${schema}`;
  return column;
}

/** One line of the "why" breakdown. */
function UnexaminedReasonLine({ reason }: { reason: SqlObjectUnexaminedReason }) {
  const count = `${reason.objects.toLocaleString('en-IN')} ${reason.objects === 1 ? 'object' : 'objects'}`;
  if (reason.origin === 'refused_at_ingest') {
    return (
      <li className="text-muted-foreground">
        <span className="text-foreground">{count}</span> refused at the collector
        boundary, so nothing was read from {reason.objects === 1 ? 'it' : 'them'} at all
        {reason.fields.length > 0 && (
          <>
            {' '}
            - the scanner sent{' '}
            {reason.fields.map((f, i) => (
              <span key={f}>
                {i > 0 && ', '}
                <code className="font-mono text-foreground">{f}</code>
              </span>
            ))}{' '}
            in a form this platform does not accept
          </>
        )}
      </li>
    );
  }
  return (
    <li className="text-muted-foreground">
      <span className="text-foreground">{count}</span>{' '}
      {reason.status === null ? 'with no status reported' : statusLabel(reason.status)}
      {reason.errorClass ? (
        <>
          {' '}
          - reason <code className="font-mono text-foreground">{reason.errorClass}</code>
        </>
      ) : (
        <> - no reason was named</>
      )}
    </li>
  );
}

/**
 * The completeness statement - the most important thing on the page.
 *
 * Rendered in BOTH directions, which is the point. `unexaminedObjects > 0` says
 * the answer is incomplete and by how much. `unexaminedObjects === 0` says, out
 * loud, that every object in scope was examined. The zero case is not an empty
 * render: a reader who sees nothing concludes "clean", and that conclusion has
 * to be earned by a measurement rather than inferred from a missing element.
 *
 * KEYED ON `unexaminedObjects`, NOT ON `unparsedObjects` (C1). A parse failure
 * and a refusal at the collector boundary are different facts with different
 * remedies, but for "was everything examined" they count the same - and the
 * refusal is the one that used to be invisible, because `unparsedObjects` counts
 * stored rows and a refused object has no row. Both terms are named below so the
 * reader can act on each.
 *
 * AND IT CARRIES THE REASONS (C2). Migration 0149 kept the reason and no query
 * could return it: the rows that carry `error_class` are the no-reference
 * sentinels with `column_name = ''`, which no `column=` filter can ever match.
 * `unexaminedByReason` is that reason's only route to a reader, which is why it
 * is rendered here rather than only on a row.
 *
 * AND IT RESPECTS PARTIAL COVERAGE (release 3.6 #24 round 10). A Java lineage
 * run that said its own coverage was partial can leave every class stored as
 * read (`unexaminedObjects` 0) while JPQL in it was read as SQL. With any such
 * run in the answer this never says "complete" or "every object was examined":
 * it says the answer may be incomplete, and why.
 */
export function CompletenessNotice({
  unexaminedObjects,
  unparsedObjects,
  refusedObjects,
  byReason,
  total,
  scope,
  partialCoverageApplications = 0,
}: {
  unexaminedObjects: number;
  unparsedObjects: number;
  refusedObjects: number;
  byReason: SqlObjectUnexaminedReason[];
  total: number;
  scope: string;
  /** Applications whose last lineage run was partial (see `partialCoverageCount`). */
  partialCoverageApplications?: number;
}) {
  const partialN = partialCoverageApplications;
  const partialSentence =
    partialN > 0 ? (
      <div data-testid="completeness-partial-coverage" className="text-warning mt-1">
        Also, for {partialN.toLocaleString('en-IN')}{' '}
        {partialN === 1 ? 'application' : 'applications'} the last lineage run reported partial
        coverage, so {partialN === 1 ? 'its' : 'their'} classes may use columns this answer does
        not show (see the partial coverage notice below).
      </div>
    ) : null;
  if (unexaminedObjects > 0) {
    return (
      <div
        data-completeness="incomplete"
        className="glass-lite border border-warning/50 rounded-md p-3 mb-4 text-xs flex items-start gap-2"
      >
        <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-medium text-foreground">
            This answer is incomplete - {unexaminedObjects.toLocaleString('en-IN')}{' '}
            {unexaminedObjects === 1 ? 'object was' : 'objects were'} in scope and could not be
            examined
          </div>
          <div className="text-muted-foreground mt-1">
            {total.toLocaleString('en-IN')}{' '}
            {total === 1 ? 'reference was' : 'references were'} found to{' '}
            <code data-testid="scope-statement" className="font-mono text-foreground">
              {scope}
            </code>
            , and separately {unexaminedObjects.toLocaleString('en-IN')}{' '}
            {unexaminedObjects === 1 ? 'object' : 'objects'} could not be examined, so{' '}
            {unexaminedObjects === 1 ? 'its' : 'their'} column{' '}
            {unexaminedObjects === 1 ? 'list is' : 'lists are'} unknown. Any of them may
            reference it without appearing below. Read this as &quot;the objects we could
            read&quot;, not &quot;all the objects&quot;.
          </div>
          {/* The two terms, named. They need different actions: an unparsed body
              is a parser problem, a refused object is a collector-contract
              problem and nothing about it reached this platform at all.
              And the SCOPE of the count is stated (low finding 13): it is
              tenant-wide, deliberately, while the list above is narrowed - an
              object whose body was never read has an unknown column list, so no
              column filter can decide whether it is relevant. "In scope" invited
              the reader to assume it had been narrowed the same way the list was. */}
          <div className="text-muted-foreground mt-1">
            {unparsedObjects.toLocaleString('en-IN')} could not be parsed
            {' · '}
            {refusedObjects.toLocaleString('en-IN')}{' '}
            {refusedObjects === 1 ? 'was' : 'were'} refused at the collector boundary
            {' · '}
            counted across every database you can see (the whole tenant, unless your role
            is limited to some business units), not narrowed by the filters above, because
            an object that was never read has an unknown column list
          </div>
          {byReason.length > 0 && (
            <ul className="mt-1.5 space-y-0.5 list-disc pl-4">
              {byReason.map((r) => (
                <UnexaminedReasonLine
                  key={`${r.origin}:${r.status ?? ''}:${r.errorClass ?? ''}:${r.fields.join(',')}`}
                  reason={r}
                />
              ))}
            </ul>
          )}
          {partialSentence}
        </div>
      </div>
    );
  }
  if (partialN > 0) {
    // Nothing failed to parse and nothing was refused, but a run said its own
    // coverage was partial. Never the green "complete" banner here.
    return (
      <div
        data-completeness="partial-coverage"
        className="glass-lite border border-warning/50 rounded-md p-3 mb-4 text-xs flex items-start gap-2"
      >
        <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-medium text-foreground">
            This answer may be incomplete - {partialN.toLocaleString('en-IN')}{' '}
            {partialN === 1 ? 'application' : 'applications'} had a partial lineage run
          </div>
          <div className="text-muted-foreground mt-1">
            {total.toLocaleString('en-IN')}{' '}
            {total === 1 ? 'reference was' : 'references were'} found to{' '}
            <code data-testid="scope-statement" className="font-mono text-foreground">
              {scope}
            </code>
            . No stored object failed to parse and none was refused, but the last lineage run of{' '}
            {partialN === 1 ? 'that application' : 'those applications'} said its coverage was
            partial: some of its SQL was not read (it sits in files the run does not read, such
            as MyBatis mappers or resource files), or query text may have been read as SQL against
            a table of the same name, so a class can be listed as fully read and still use columns
            this answer does not show. Read this as &quot;what the runs could establish&quot;. The
            reasons are in the partial coverage notice below.
          </div>
        </div>
      </div>
    );
  }
  return (
    <div
      data-completeness="examined-all"
      className="glass-lite border border-success/40 rounded-md p-3 mb-4 text-xs flex items-start gap-2"
    >
      <CheckCircle2 className="h-4 w-4 text-success shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="font-medium text-foreground">Every object in scope was examined</div>
        <div className="text-muted-foreground mt-1">
          No object failed to parse and none was refused at the collector boundary, so the list
          below is the complete set of objects this platform can see that touch{' '}
          <code data-testid="scope-statement" className="font-mono text-foreground">
            {scope}
          </code>{' '}
          - not merely the ones it managed to read.
        </div>
        <div data-testid="completeness-jpql-limit" className="text-muted-foreground mt-1">
          {JPQL_LIMIT_SENTENCE}
        </div>
        <div data-testid="completeness-code-scope-limit" className="text-muted-foreground mt-1">
          {CODE_SCOPE_LIMIT_SENTENCE}
        </div>
      </div>
    </div>
  );
}

/** Icon per object kind. `job_step` gets the one that does not look like a file. */
/**
 * For a session limited to some business units: the answer holds only the
 * references whose table could be tied to one of them, and says how many were
 * held back (a count, never names). Round 3 of the database-object lineage PR
 * (the review at 81601fc): the answer used to be cut with nothing said.
 */
export function UnitScopeNotice({
  businessUnitScoped,
  hiddenReferences,
}: {
  businessUnitScoped: boolean | undefined;
  hiddenReferences: number | undefined;
}) {
  if (!businessUnitScoped) return null;
  const n = hiddenReferences ?? 0;
  return (
    <div data-testid="unit-scope-notice" className="glass-lite rounded-md border border-border/40 px-3 py-2 mb-3 text-xs">
      <div className="text-foreground/90">
        You see only references to tables of your business units.
      </div>
      {n > 0 ? (
        <div data-testid="unit-scope-hidden" className="text-warning mt-1">
          {n.toLocaleString('en-IN')} {n === 1 ? 'reference' : 'references'} in the databases you
          can see {n === 1 ? 'is' : 'are'} not shown, because {n === 1 ? 'its' : 'their'} table
          could not be tied to one of your business units: it belongs to another unit, is a view
          or a table that was not scanned, or is named without a schema that we could resolve.
        </div>
      ) : (
        <div data-testid="unit-scope-none-hidden" className="text-muted-foreground mt-1">
          No reference in the databases you can see was held back.
        </div>
      )}
      <div className="text-muted-foreground mt-1">
        Databases where you can see no table are not counted.
      </div>
      <div data-testid="unit-scope-application-code" className="text-muted-foreground mt-1">
        Java classes (application code) are not tied to business units yet, so they are shown, and
        counted, only for people who can see every business unit.
      </div>
    </div>
  );
}

function kindIcon(kind: string) {
  if (kind === 'job_step') return Workflow;
  if (kind === 'view' || kind === 'materialized_view') return ListTree;
  return FileCode;
}

/**
 * One reference.
 *
 * Three things this row must never do: present an unresolved reference as a
 * located one, let a `referenced` row imply data moved, or show a partial
 * source locator as if it were complete.
 */
/**
 * What each partial-coverage code means, in words. Counts come from the run;
 * no name, statement or message ever crosses the wire (closed codes only).
 */
const COVERAGE_WARNING_COPY: Record<string, (n: number) => string> = {
  jpa_entities_not_in_tree: (n) =>
    `the code uses JPA (${n.toLocaleString('en-IN')} JPQL ${n === 1 ? 'call' : 'calls'}) but no entity class was in the source read, so JPQL held in one file and run from another may have been read as SQL`,
  jpql_entities_not_found: (n) =>
    `${n.toLocaleString('en-IN')} ${n === 1 ? 'name' : 'names'} that JPQL queries ${n === 1 ? 'is' : 'are'} not an entity in the source read, so JPQL naming ${n === 1 ? 'it' : 'them'} elsewhere may have been read as SQL`,
  orm_mapping_files_not_read: (n) =>
    `${n.toLocaleString('en-IN')} orm.xml or hbm.xml ${n === 1 ? 'file was' : 'files were'} not read, so entity names ${n === 1 ? 'it maps' : 'they map'} are missing`,
  unknown_entity_statements: (n) =>
    `${n.toLocaleString('en-IN')} ${n === 1 ? 'statement names' : 'statements name'} an entity or table that is in neither the source nor its DDL; ${n === 1 ? 'it was' : 'they were'} counted as not read, and the entities ${n === 1 ? 'it names are' : 'they name are'} outside the source read`,
  ddl_files_not_read: (n) =>
    `${n.toLocaleString('en-IN')} .sql ${n === 1 ? 'file was' : 'files were'} not read for table definitions`,
  // Round 12: SQL the run knows is in the application and did not read.
  mybatis_mapper_files_not_read: (n) =>
    `${n.toLocaleString('en-IN')} MyBatis mapper XML ${n === 1 ? 'file was' : 'files were'} not read, so the SQL in ${n === 1 ? 'it is' : 'them is'} not in this answer`,
  other_language_files_not_read: (n) =>
    `${n.toLocaleString('en-IN')} Kotlin, Groovy or Scala ${n === 1 ? 'file was' : 'files were'} not read (only Java is), so the SQL in ${n === 1 ? 'it is' : 'them is'} not in this answer`,
  resource_files_not_read: (n) =>
    `${n.toLocaleString('en-IN')} resource ${n === 1 ? 'file' : 'files'} (.sql, .properties, .yml or .xml) ${n === 1 ? 'was' : 'were'} not read, so SQL a class loads from ${n === 1 ? 'it' : 'them'} by name is not in this answer`,
  links_not_followed: (n) =>
    `${n.toLocaleString('en-IN')} ${n === 1 ? 'link' : 'links'} to source ${n === 1 ? 'was' : 'were'} not followed, so the code behind ${n === 1 ? 'it was' : 'them was'} not read`,
  run_not_finished: (n) =>
    `the run did not finish: ${n.toLocaleString('en-IN')} ${n === 1 ? 'class was' : 'classes were'} still to be sent, so ${n === 1 ? 'it may show' : 'they may show'} an earlier run's lineage or none`,
};

/**
 * The one case the Java lineage run cannot tell apart, said where a reader sees
 * it (round 10): JPQL naming an entity whose name is a single lower-case word,
 * using only single-word properties, is indistinguishable from native SQL on a
 * table of that name. When the entity class is not in the source read, such a
 * query is read as SQL and the run does not flag it.
 */
export const JPQL_LIMIT_SENTENCE =
  'Known limit for Java application code: a JPQL query whose entity name is a single lower-case word (for example "orders") and that uses only single-word property names reads exactly like SQL. If that entity class was not in the source read, the query is read as SQL against a table of that name and is not flagged.';

/**
 * Round 13 of 3.6 #24: what a Java lineage run cannot see, said on the green
 * all-clear too, so "every object was examined" is never read as "every
 * column use in the code is known". Round 14: the sentence claims only what
 * the rule does. The rule recognises a closed list of calls (listed in
 * docs/INVENTORY.yaml); SQL handed to anything else is not seen. Round 24
 * (the review at 5a6a8183): a field another class may assign is counted as
 * not read; what stays unseen (reflection, a framework binding with no
 * annotation the tool knows) and what a run never clears (a class deleted
 * from the source) are said here, not only in INVENTORY. Shown to every
 * tenant: the page cannot tell whether a tenant has Java applications without
 * a further read, and on a tenant without one the sentences are still true.
 */
export const CODE_SCOPE_LIMIT_SENTENCE =
  'Known limits for Java application code: a class listed as fully read means every SQL call this tool recognises in it was read. SQL that is not a literal (built or loaded at run time, from a property, a file or a parameter) is counted as not read when it is passed to one of the calls the tool recognises in JDBC, JPA, Hibernate, Spring JDBC, Spring Data, R2DBC, MyBatis, jOOQ, QueryDSL, Jdbi, Commons DbUtils, Ebean, sql2o or EclipseLink; that list is closed. A field holding a SQL literal that is not private and not final is counted as not read when another class of the source read could assign it, or when it carries an annotation a framework binds from data (Spring @Value, Jackson @JsonProperty, JPA @Column and others the tool knows). A field set in a way the tool cannot see in the source (by reflection, or by a framework that binds it with no such annotation, for example Jackson on a public field) is read as its literal, so such a class can be listed as fully read with the columns of that literal, not of the SQL it runs. SQL passed to any other call, another library or a helper class in another file is not seen, so such a class can still be listed as fully read. A class deleted from the source keeps its stored rows and is still listed: a later run replaces the rows of the classes it finds, not the others. Files outside the module a run scanned (another module, a library jar, a database table of queries) are not seen at all.';

/** How many applications had a partial run: the larger of the stated total and the listed sources. */
export function partialCoverageCount(a: {
  partialCoverageSources?: unknown[] | undefined;
  partialCoverageTotal?: number | undefined;
}): number {
  return Math.max(a.partialCoverageTotal ?? 0, (a.partialCoverageSources ?? []).length);
}

function coverageWarningText(w: { code: string; count: number }): string {
  const copy = COVERAGE_WARNING_COPY[w.code];
  return copy ? copy(w.count) : `${w.count.toLocaleString('en-IN')} (${w.code})`;
}

/** The key a row and a partial source share: the four-part location. */
export function locationKey(l: { sourceEngine: string; sourceHost: string; sourcePort: number; sourceDatabase: string }): string {
  return JSON.stringify([l.sourceEngine, l.sourceHost, l.sourcePort, l.sourceDatabase]);
}

/**
 * Release 3.6 #24 round 9: a Java application whose last lineage run said its
 * own coverage was PARTIAL. Its classes can be listed as fully read while some
 * JPQL in it was read as SQL, so the page says so, above the answer, with the
 * run's reasons. Rendered only when there is such a run; not narrowed by the
 * column asked about, since the run may have misread any column.
 */
export function PartialCoverageNotice({
  sources,
  total,
}: {
  sources: SqlObjectPartialCoverageSource[] | undefined;
  total: number | undefined;
}) {
  const list = sources ?? [];
  const n = partialCoverageCount({ partialCoverageSources: list, partialCoverageTotal: total });
  if (n === 0) return null;
  return (
    <div
      data-testid="partial-coverage-notice"
      role="status"
      className="glass-lite rounded-md border border-warning/50 px-3 py-2 mb-3 text-xs flex items-start gap-2"
    >
      <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="font-medium text-foreground">
          Partial coverage: for {n.toLocaleString('en-IN')}{' '}
          {n === 1 ? 'application' : 'applications'}, the last lineage run was not complete, so{' '}
          {n === 1 ? 'its' : 'their'} classes may use columns this answer does not show, even where
          a class is listed as fully read.
        </div>
        <ul className="mt-1 space-y-1 text-muted-foreground">
          {list.map((s) => (
            <li key={locationKey(s)} data-testid="partial-coverage-source">
              <span className="font-mono text-foreground">{s.sourceDatabase}</span>
              {s.sourceEngine === 'java_source' ? ' (Java application)' : ` (${s.sourceEngine}://${s.sourceHost})`}:{' '}
              {s.warnings.map(coverageWarningText).join('; ')}.
            </li>
          ))}
        </ul>
        {n > list.length && (
          <div data-testid="partial-coverage-more" className="mt-1 text-muted-foreground">
            And {(n - list.length).toLocaleString('en-IN')} more not listed here.
          </div>
        )}
        <div data-testid="partial-coverage-limit" className="mt-1 text-muted-foreground">
          {JPQL_LIMIT_SENTENCE} {CODE_SCOPE_LIMIT_SENTENCE}
        </div>
      </div>
    </div>
  );
}

export function SqlObjectReferenceRow({
  reference,
  partialRun = false,
}: {
  reference: SqlObjectReference;
  /** The row's location had a partial run: its status alone does not say it was fully read. */
  partialRun?: boolean;
}) {
  const KindIcon = kindIcon(reference.objectKind);
  const derived = reference.relationship === 'derived';
  const suspect = isUntrustworthy(reference.status);
  return (
    <div
      data-resolved={reference.resolved ? 'true' : 'false'}
      className={
        'px-4 py-3 border-b border-border/30 border-l-2 hover:bg-muted/40 transition-colors ' +
        (reference.resolved ? 'border-l-success/50' : 'border-l-warning/70 bg-warning/5')
      }
    >
      {/* Line 1 - what the object is, and whether we could read it. */}
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="glass" className="text-[9px] uppercase tracking-wide">
          {kindLabel(reference.objectKind)}
        </Badge>
        <span className="font-mono text-sm text-foreground">
          <KindIcon className="h-3.5 w-3.5 inline-block mr-1.5 opacity-60 align-[-2px]" />
          {reference.objectSchema}.{reference.objectName}
        </span>
        <span className="flex-1" />
        {derived ? (
          <Badge variant="info" className="text-[9px]">
            Data flowed from this column
          </Badge>
        ) : (
          <Badge variant="glass" className="text-[9px]">
            Mentions this column
          </Badge>
        )}
        <Badge variant="glass" className="text-[9px] font-mono">
          {reference.direction}
        </Badge>
        {suspect ? (
          <Badge variant="warning" className="text-[9px]">
            {statusLabel(reference.status)}
          </Badge>
        ) : (
          <Badge variant="success" className="text-[9px]">
            {statusLabel(reference.status)}
          </Badge>
        )}
        {partialRun && (
          <Badge data-testid="row-partial-run" variant="warning" className="text-[9px]">
            Partial coverage run
          </Badge>
        )}
      </div>

      {/* Line 2 - WHERE it lives. All four segments, always. */}
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        <span className="font-mono flex items-center gap-1.5">
          <Server className="h-3 w-3 opacity-60" />
          {formatSourceLocator(reference)}
        </span>
        <span className="flex items-center gap-1.5">
          <Database className="h-3 w-3 opacity-60" />
          {reference.resolved ? (
            <>
              column <code className="font-mono text-foreground">{formatColumnSite(reference)}</code>
            </>
          ) : (
            <>
              column <code className="font-mono text-foreground">{reference.columnName}</code>{' '}
              <span className="text-warning">(enclosing table not resolved)</span>
            </>
          )}
        </span>
        {derived && (
          <span className="flex items-center gap-1.5">
            <ArrowRight className="h-3 w-3 opacity-60" />
            {reference.targetColumn ? (
              <>
                into <code className="font-mono text-foreground">{reference.targetColumn}</code>
              </>
            ) : (
              /* Should be unreachable: ingest refuses a `derived` row whose
                 target is null, so the pairing is an invariant. Kept as a
                 visible guard rather than an empty render, because if that
                 invariant is ever broken the reader must SEE that the
                 destination is missing - "derived" with a silent blank reads
                 as "the data went nowhere", which is a claim this row does not
                 support. A contract violation should look like one. */
              <span className="text-warning">target column not reported by the API</span>
            )}
          </span>
        )}
      </div>

      {/* Line 3 - the caveats, only when there are any. */}
      {(!reference.resolved || suspect || reference.errorClass) && (
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[10px]">
          {!reference.resolved && (
            <span className="inline-flex items-center gap-1 text-warning">
              <HelpCircle className="h-3 w-3" />
              Reference seen but not fully located - typically an unqualified column in a
              procedure body, or a cross-database reference this row cannot carry. Not a
              confirmed location.
            </span>
          )}
          {reference.status === 'partially_parsed' && (
            <span className="inline-flex items-center gap-1 text-warning">
              <AlertTriangle className="h-3 w-3" />
              This object contributed some references and lost others, so its own list is
              incomplete.
            </span>
          )}
          {reference.errorClass && (
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              reason: <code className="font-mono text-foreground">{reference.errorClass}</code>
            </span>
          )}
        </div>
      )}

      {/* Provenance - which parser read this, in which dialect, when. */}
      <div className="mt-1 text-[10px] text-muted-foreground">
        read {new Date(reference.parsedAt).toLocaleString('en-IN')}
        {reference.dialect ? ` · dialect ${reference.dialect}` : ''}
        {reference.parserVersion ? ` · ${reference.parserVersion}` : ''}
      </div>
    </div>
  );
}

/**
 * What the scanner reads and what it cannot. Stated on the page so that an
 * object missing from an answer is not read as "touches nothing".
 */
export function CoverageNote() {
  return (
    <div
      data-testid="coverage-note"
      className="glass-lite border border-border/40 rounded-md p-3 mb-6 text-xs text-muted-foreground"
    >
      <div className="font-medium text-foreground mb-1">What this can and cannot see</div>
      <ul className="list-disc pl-4 space-y-0.5">
        <li>
          Read: views, procedures and functions on PostgreSQL, SQL Server, MySQL and Oracle;
          materialized views and triggers on PostgreSQL; triggers on MySQL and SQL Server;
          Oracle package bodies; SQL Server Agent job steps.
        </li>
        <li>Oracle triggers and Oracle materialized views are not read yet.</li>
        <li>
          SQL built at run time (dynamic SQL) is not followed: an object that builds its query
          as text shows only what it names directly.
        </li>
        <li>
          SQL Server triggers are listed, but the parser returns no column references from
          their bodies today, so a SQL Server trigger showing no columns is not proof that it
          touches none.
        </li>
        <li>
          An object past a scan limit (object count, body size or time) is listed as not
          analysed, never dropped.
        </li>
      </ul>
    </div>
  );
}

const HOP_COPY: Record<number, string> = {
  1: 'Reads or writes the table directly',
  2: 'Through a view of the table',
  3: 'Through a view of a view',
};

/** One object step in a lineage answer. */
export function LineageEdgeRow({ edge }: { edge: SqlObjectLineageEdge }) {
  const KindIcon = kindIcon(edge.objectKind);
  return (
    <li
      data-hop={edge.hop}
      className="px-3 py-2 border-b border-border/20 flex flex-wrap items-center gap-2 text-xs"
    >
      <Badge variant="glass" className="text-[9px] uppercase tracking-wide">
        {kindLabel(edge.objectKind)}
      </Badge>
      <span className="font-mono text-foreground">
        <KindIcon className="h-3.5 w-3.5 inline-block mr-1.5 opacity-60 align-[-2px]" />
        {edge.objectSchema}.{edge.objectName}
      </span>
      <span className="text-muted-foreground">
        {edge.direction === 'write' ? 'writes' : 'reads'}{' '}
        <code className="font-mono text-foreground">
          {edge.relationSchema}.{edge.relationName}.{edge.columnName}
        </code>
      </span>
      {edge.relationship === 'derived' && edge.targetColumn && (
        <span className="text-muted-foreground">
          into <code className="font-mono text-foreground">{edge.targetColumn}</code>
        </span>
      )}
      <span className="flex-1" />
      {edge.personalData === true && (
        <Badge variant="warning" className="text-[9px]" data-testid="personal-data-edge">
          Personal data column
        </Badge>
      )}
      {isUntrustworthy(edge.status) && (
        <Badge variant="warning" className="text-[9px]">
          {statusLabel(edge.status)}
        </Badge>
      )}
    </li>
  );
}

/** One table's lineage. */
export function LineageTableCard({ table }: { table: SqlObjectLineageTable }) {
  const locator =
    table.engine && table.host && table.port !== null && table.database
      ? `${table.engine}://${table.host}:${table.port}/${table.database}`
      : null;
  const hops = [1, 2, 3].filter((h) => table.edges.some((e) => e.hop === h));
  // A MySQL scan reads the whole server in one pass and does not record the
  // database of each object, so its counts cover the server, not one database.
  const where = table.engine === 'mysql' ? 'on this server' : 'in this database';
  return (
    <div data-testid="lineage-table" className="glass-lite rounded-md border border-border/40 mb-4">
      <div className="px-3 py-2 border-b border-border/40">
        <div className="font-mono text-sm text-foreground">{table.relation ?? table.displayName}</div>
        {locator && <div className="font-mono text-[11px] text-muted-foreground">{locator}</div>}
      </div>
      {!table.lineageAvailable ? (
        <div data-testid="lineage-unavailable" className="px-3 py-3 text-xs text-warning">
          This table was scanned before its server and database were recorded, so its lineage
          cannot be looked up. A new scan of the table fixes this. This is not a statement
          that no object uses it.
        </div>
      ) : (
        <>
          <div className="px-3 py-2 text-xs">
            <div className="text-muted-foreground mb-1">Personal data columns in this table</div>
            {table.personalDataColumns.length === 0 ? (
              <div className="text-muted-foreground">
                None found by classification so far.
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {table.personalDataColumns.map((c) => (
                  <Badge key={c.columnName} variant="glass" className="text-[10px] font-mono">
                    {c.columnName}
                    {c.labels.length > 0 ? ` (${c.labels.join(', ')})` : ''}
                  </Badge>
                ))}
              </div>
            )}
            {table.personalDataColumnsTruncated && (
              <div className="text-warning mt-1">Only the first 200 columns are listed.</div>
            )}
          </div>
          {table.edges.length === 0 ? (
            <div data-testid="lineage-no-edges" className="px-3 py-3 text-xs text-muted-foreground">
              No object we could read uses this table.
            </div>
          ) : (
            hops.map((h) => (
              <div key={h}>
                <div className="px-3 pt-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                  {HOP_COPY[h] ?? `Step ${h}`}
                </div>
                <ul>
                  {table.edges
                    .filter((e) => e.hop === h)
                    .map((e, i) => (
                      <LineageEdgeRow
                        key={`${e.objectKind}:${e.objectSchema}.${e.objectName}:${e.relationName}.${e.columnName}:${e.direction}:${i}`}
                        edge={e}
                      />
                    ))}
                </ul>
              </div>
            ))
          )}
          <div data-testid="lineage-caveats" className="px-3 py-2 text-[11px] text-muted-foreground space-y-0.5">
            {table.truncated.edges && (
              <div className="text-warning">
                Only the first 200 steps are shown. More objects use this table.
              </div>
            )}
            {table.truncated.relations && (
              <div className="text-warning">
                Only 100 views were followed at one step. Objects reached through the others are
                not shown.
              </div>
            )}
            {table.unplacedObjects > 0 && (
              <div>
                {table.unplacedObjects.toLocaleString('en-IN')}{' '}
                {table.unplacedObjects === 1 ? 'object names' : 'objects name'} a table of this
                name without a schema, so {table.unplacedObjects === 1 ? 'it' : 'they'} may or may
                not use this table and {table.unplacedObjects === 1 ? 'is' : 'are'} not listed.
              </div>
            )}
            {table.unexaminedObjects > 0 ? (
              <div data-testid="lineage-incomplete" className="text-warning">
                {table.unexaminedObjects.toLocaleString('en-IN')}{' '}
                {table.unexaminedObjects === 1 ? 'object' : 'objects'} {where} could not
                be examined ({table.unparsedObjects.toLocaleString('en-IN')} not parsed,{' '}
                {table.refusedObjects.toLocaleString('en-IN')} refused), so{' '}
                {table.unexaminedObjects === 1 ? 'it' : 'any of them'} may use this table without
                appearing here.
              </div>
            ) : (
              <div data-testid="lineage-complete">
                Every object read {where} was examined.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

type LineagePhase =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'loaded'; answer: SqlObjectLineageResponse }
  | { kind: 'error'; message: string };

/**
 * Personal data lineage (Bank: database-object lineage): for a scanned table,
 * which objects read or write it, directly or through views, and which of its
 * columns hold personal data.
 */
export function PersonalDataLineage() {
  const [table, setTable] = useState('');
  const [schema, setSchema] = useState('');
  const [depth, setDepth] = useState(2);
  const [phase, setPhase] = useState<LineagePhase>({ kind: 'idle' });

  const run = useCallback(async (t: string, s: string, d: number) => {
    if (!t) return;
    setPhase({ kind: 'loading' });
    try {
      const answer = await getSqlObjectLineage({
        table: t,
        ...(s ? { schema: s } : {}),
        depth: d,
        limit: 5,
      });
      setPhase({ kind: 'loaded', answer });
    } catch (err) {
      setPhase({ kind: 'error', message: err instanceof Error ? err.message : 'Lookup failed' });
    }
  }, []);

  return (
    <GlassCard className="p-5 mb-6" data-testid="personal-data-lineage">
      <CardHeader className="p-0 mb-3">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Workflow className="h-4 w-4 text-primary" /> Personal data lineage
        </CardTitle>
        <CardDescription>
          Name a scanned table. This shows its personal data columns and every database object
          that reads or writes it, directly or through views, up to three steps away. Only tables
          you are allowed to see are answered.
        </CardDescription>
      </CardHeader>
      <form
        className="grid grid-cols-1 md:grid-cols-4 gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void run(table.trim(), schema.trim(), depth);
        }}
      >
        <input
          aria-label="Lineage table"
          placeholder="table (e.g. customers)"
          value={table}
          onChange={(e) => setTable(e.target.value)}
          className="glass-lite rounded-md px-3 py-2 bg-transparent border border-border/40 font-mono text-xs text-foreground"
        />
        <input
          aria-label="Lineage schema (optional)"
          placeholder="schema (optional)"
          value={schema}
          onChange={(e) => setSchema(e.target.value)}
          className="glass-lite rounded-md px-3 py-2 bg-transparent border border-border/40 font-mono text-xs text-foreground"
        />
        <select
          aria-label="Steps to follow"
          value={depth}
          onChange={(e) => setDepth(Number(e.target.value))}
          className="glass-lite rounded-md px-3 py-2 bg-transparent border border-border/40 text-xs text-foreground"
        >
          <option value={1}>Direct use only</option>
          <option value={2}>Through views (2 steps)</option>
          <option value={3}>Through views of views (3 steps)</option>
        </select>
        <Button type="submit" disabled={phase.kind === 'loading' || !table.trim()}>
          {phase.kind === 'loading' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Workflow className="h-4 w-4" />}
          Show lineage
        </Button>
      </form>
      <div className="mt-4">
        {phase.kind === 'loading' && <LoadingState caption="Following the table…" />}
        {phase.kind === 'error' && (
          <ErrorState title="Could not look up this table" detail={phase.message} />
        )}
        {phase.kind === 'loaded' &&
          (phase.answer.tables.length === 0 ? (
            <div data-testid="lineage-no-table" className="text-xs text-muted-foreground">
              No scanned table of that name that you can see.
              {phase.answer.businessUnitScoped
                ? ' Your role sees some business units only; a table of another unit is not shown.'
                : ''}
            </div>
          ) : (
            <>
              {phase.answer.tablesTotal > phase.answer.tables.length && (
                <div data-testid="lineage-more-tables" className="text-xs text-warning mb-2">
                  {phase.answer.tablesTotal.toLocaleString('en-IN')} tables match; the first{' '}
                  {phase.answer.tables.length} are shown. Add a schema to narrow it.
                </div>
              )}
              {phase.answer.tables.map((t) => (
                <LineageTableCard key={t.assetId} table={t} />
              ))}
            </>
          ))}
      </div>
    </GlassCard>
  );
}

/**
 * The filters an answer was fetched WITH.
 *
 * H4: `run` used to close over the live `column` / `table` / `schema` state while
 * Prev/Next passed the OLD offset, so typing `customers` into the table box and
 * then pressing Next asked for `?column=pan_number&table=customers&offset=100`
 * against an answer with 30 references - and the page rendered "No object in
 * scope references pan_number" beside "Every object in scope was examined", with
 * the pager hidden so there was no way back. An answer and the question it
 * answers have to travel together.
 */
export interface SqlObjectFilters {
  column: string;
  table: string;
  schema: string;
}

/** A search has either not been run yet, or it has. These are not the same state. */
type Phase =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | {
      kind: 'loaded';
      answer: SqlObjectReferencesResponse;
      /** The SNAPSHOT the answer was fetched with, not whatever is in the boxes now. */
      filters: SqlObjectFilters;
      offset: number;
    }
  | { kind: 'error'; message: string };

function sameFilters(a: SqlObjectFilters, b: SqlObjectFilters): boolean {
  return a.column === b.column && a.table === b.table && a.schema === b.schema;
}

export function DiscoverySqlObjectUsage() {
  const [column, setColumn] = useState('');
  const [table, setTable] = useState('');
  const [schema, setSchema] = useState('');
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });

  /**
   * Fetch one page FOR A GIVEN SET OF FILTERS.
   *
   * The filters are a PARAMETER, not a closure over the inputs (H4). The pager
   * passes the snapshot the current answer was fetched with, so pressing Next can
   * only ever walk deeper into that same answer; the form passes what the user
   * just typed, always at offset 0. With the filters closed over, Next mixed a
   * new question with an old offset and the page presented the result as an
   * answer to neither.
   */
  const run = useCallback(async (offset: number, filters: SqlObjectFilters) => {
    if (!filters.column) return;
    setPhase({ kind: 'loading' });
    try {
      const answer = await getSqlObjectReferences({
        column: filters.column,
        ...(filters.table ? { table: filters.table } : {}),
        ...(filters.schema ? { schema: filters.schema } : {}),
        limit: PAGE_SIZE,
        offset,
      });
      setPhase({ kind: 'loaded', answer, filters, offset });
    } catch (err) {
      setPhase({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Lookup failed',
      });
    }
  }, []);

  /** What is in the boxes right now - the question a submit would ask. */
  const live: SqlObjectFilters = {
    column: column.trim(),
    table: table.trim(),
    schema: schema.trim(),
  };

  const loaded = phase.kind === 'loaded' ? phase : null;
  const answer = loaded?.answer;
  const partialLocations = new Set((answer?.partialCoverageSources ?? []).map(locationKey));
  /** Applications whose last lineage run was partial: no completeness claim may ignore them. */
  const partialN = answer ? partialCoverageCount(answer) : 0;
  /**
   * The boxes no longer match the answer on screen. The answer is not wrong - it
   * is the answer to the previous question - so it stays, but paging it would be
   * paging a question nobody asked, and the pager is withdrawn until the user
   * asks the new one.
   */
  const filtersChanged = loaded !== null && !sameFilters(loaded.filters, live);
  /**
   * This page has no rows but the answer has references: the offset is past the
   * end. Reachable without touching a filter whenever a re-scan shrinks the
   * answer between two pages - which `replaceSqlObjectReferences` rewriting the
   * rows wholesale makes the normal case, not an edge case.
   */
  const pastTheEnd = answer !== undefined && answer.references.length === 0 && answer.total > 0;
  /**
   * Shown whenever there is somewhere to go: a second page ahead, or any page
   * behind. `answer.total > PAGE_SIZE` alone hid the pager exactly when the
   * answer had shrunk under one page while the offset was past it, which is the
   * state with no way back.
   */
  const pagerUseful = loaded !== null && (loaded.offset > 0 || (answer?.total ?? 0) > PAGE_SIZE);
  const scope = loaded ? describeScope(loaded.filters) : '';

  return (
    <PageContainer>
      <PageHeader
        title="Impact Analysis: where a column is used in SQL"
        subtitle="Views, materialized views, procedures, functions, triggers, Oracle package bodies, SQL Agent job steps and Java classes that touch a given column. Name the column and this answers which objects reach it, where each object lives, and - just as importantly - how many objects could not be read at all."
        badges={[
          ...(phase.kind === 'loading'
            ? [{ label: 'Looking up', variant: 'glass' as const }]
            : phase.kind === 'error'
              ? [{ label: 'Load error', variant: 'warning' as const }]
              : phase.kind === 'loaded'
                ? [{ label: 'Live', variant: 'success' as const }]
                : [{ label: 'Ready', variant: 'glass' as const }]),
        ]}
      />

      {/* The question */}
      <GlassCard className="p-5 mb-6">
        <CardHeader className="p-0 mb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Search className="h-4 w-4 text-primary" /> Look up a column
          </CardTitle>
          <CardDescription>
            The column name is required. Narrow by table or schema when the same column name
            appears in more than one place.
          </CardDescription>
        </CardHeader>
        <form
          className="grid grid-cols-1 md:grid-cols-4 gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            // Always offset 0: a new question starts at its own first page.
            void run(0, live);
          }}
        >
          <input
            aria-label="Column name"
            placeholder="column name (e.g. pan_number)"
            value={column}
            onChange={(e) => setColumn(e.target.value)}
            className="glass-lite rounded-md px-3 py-2 text-sm bg-transparent border border-border/40 font-mono text-xs text-foreground"
          />
          <input
            aria-label="Table (optional)"
            placeholder="table (optional)"
            value={table}
            onChange={(e) => setTable(e.target.value)}
            className="glass-lite rounded-md px-3 py-2 text-sm bg-transparent border border-border/40 font-mono text-xs text-foreground"
          />
          <input
            aria-label="Schema (optional)"
            placeholder="schema (optional)"
            value={schema}
            onChange={(e) => setSchema(e.target.value)}
            className="glass-lite rounded-md px-3 py-2 text-sm bg-transparent border border-border/40 font-mono text-xs text-foreground"
          />
          <Button type="submit" disabled={phase.kind === 'loading' || !column.trim()} data-action="disc.dspm_sql_objects.a01">
            {phase.kind === 'loading' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            Find objects
          </Button>
        </form>
      </GlassCard>

      <CoverageNote />

      <PersonalDataLineage />

      {phase.kind === 'loading' && <LoadingState caption="Searching SQL objects…" />}

      {phase.kind === 'error' && (
        <ErrorState
          title="Could not look up this column"
          detail={phase.message}
          onRetry={() => void run(0, live)}
        />
      )}

      {/* NOT-YET-ASKED. Distinct from "asked and found nothing": this page has
          measured nothing, so it asserts nothing about the estate. */}
      {phase.kind === 'idle' && (
        <div className="glass-lite rounded-md p-8 text-center text-sm text-muted-foreground">
          <Search className="h-8 w-8 mx-auto mb-3 opacity-40" />
          <div className="font-medium text-foreground/90">No search performed yet</div>
          <div className="mt-1">
            Enter a column name above. Until then this page makes no claim about which objects
            touch anything - nothing has been looked up.
          </div>
        </div>
      )}

      {loaded && answer && (
        <>
          {/* The completeness statement sits immediately next to the total, in
              both directions, and is keyed on the objects that were not
              EXAMINED - not only on the ones whose parse failed (C1). */}
          <CompletenessNotice
            unexaminedObjects={answer.unexaminedObjects}
            unparsedObjects={answer.unparsedObjects}
            refusedObjects={answer.refusedObjects}
            byReason={answer.unexaminedByReason}
            total={answer.total}
            scope={scope}
            partialCoverageApplications={partialN}
          />
          <UnitScopeNotice
            businessUnitScoped={answer.businessUnitScoped}
            hiddenReferences={answer.hiddenReferences}
          />
          <PartialCoverageNotice
            sources={answer.partialCoverageSources}
            total={answer.partialCoverageTotal}
          />

          {/* The answer on screen no longer matches the boxes. Said plainly,
              because the alternative - leaving the pager live - let Next ask the
              new question at the old offset. */}
          {filtersChanged && (
            <div
              data-testid="filters-changed"
              className="glass-lite border border-warning/50 rounded-md p-3 mb-4 text-xs flex items-start gap-2"
            >
              <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
              <div className="flex-1 text-muted-foreground">
                <span className="font-medium text-foreground">
                  The filters have changed since this answer was fetched.
                </span>{' '}
                What is below still answers{' '}
                <code className="font-mono text-foreground">{scope}</code>. Press{' '}
                <span className="text-foreground">Find objects</span> to ask the new question -
                paging this answer would mix the two.
              </div>
            </div>
          )}

          {/* ALL FOUR TILES COUNT THE SAME SET (H5). `unresolvedReferences` and
              `derivedReferences` used to be computed by filtering THIS PAGE while
              `total` beside them counted the whole answer, so 412 derivations
              across 30 pages read as "Data actually flowed: 4". They are now
              scope-wide counts from the API, over the predicates `total` uses. */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div data-testid="kpi-total">
              <KpiCard
                label="References found"
                value={answer.total}
                icon={ListTree}
                hint={
                  answer.total > answer.references.length
                    ? `showing ${answer.references.length} of ${answer.total.toLocaleString('en-IN')}`
                    : 'complete page'
                }
              />
            </div>
            <div data-testid="kpi-unexamined">
              <KpiCard
                label="Objects not examined"
                value={answer.unexaminedObjects}
                icon={AlertTriangle}
                glow={answer.unexaminedObjects > 0 || partialN > 0 ? 'high' : 'low'}
                hint={
                  answer.unexaminedObjects > 0
                    ? `${answer.unparsedObjects} unparsed · ${answer.refusedObjects} refused` +
                      (partialN > 0 ? ' · and partial coverage, see notice' : '')
                    : partialN > 0
                      ? 'completeness not established - see partial coverage'
                      : 'none - all examined'
                }
              />
            </div>
            <div data-testid="kpi-unresolved">
              <KpiCard
                label="Unconfirmed locations"
                value={answer.unresolvedReferences}
                icon={HelpCircle}
                hint="seen but not fully located, across the whole answer"
              />
            </div>
            <div data-testid="kpi-derived">
              <KpiCard
                label="Data actually flowed"
                value={answer.derivedReferences}
                icon={ArrowRight}
                hint="derived, not just referenced, across the whole answer"
              />
            </div>
          </div>

          <GlassCard className="p-0 overflow-hidden">
            <div className="px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/40 flex items-center justify-between gap-2">
              <span>
                Objects touching <code className="font-mono normal-case">{scope}</code>
              </span>
              {/* The rows ACTUALLY on screen. The old form printed
                  `offset + 1`-`offset + rows` unconditionally, so an off-the-end
                  page rendered "101-100 of 30" - a range whose end precedes its
                  start. */}
              <span data-testid="page-range" className="normal-case">
                {answer.references.length > 0
                  ? `${loaded.offset + 1}-${loaded.offset + answer.references.length} of ${answer.total.toLocaleString('en-IN')}`
                  : ''}
              </span>
            </div>

            {answer.references.length === 0 ? (
              pastTheEnd ? (
                /* PAST THE END. Not "nothing references this column": the answer
                   has references, this page is simply beyond them. Keeping the
                   two apart is the difference between "we looked and found
                   nothing" and "you are looking in the wrong place". */
                <div data-testid="past-the-end" className="px-4 py-8 text-center text-sm">
                  <div className="font-medium text-foreground/90">
                    This page is past the end of the answer
                  </div>
                  <div className="mt-1 text-muted-foreground text-xs max-w-xl mx-auto">
                    {answer.total.toLocaleString('en-IN')}{' '}
                    {answer.total === 1 ? 'reference' : 'references'} to{' '}
                    <code className="font-mono">{scope}</code>{' '}
                    {answer.total === 1 ? 'exists' : 'exist'}, and none of{' '}
                    {answer.total === 1 ? 'it' : 'them'} falls on this page. The answer may have
                    shrunk since the previous page was fetched - a re-scan rewrites these rows
                    wholesale. Use Previous, or search again.
                  </div>
                </div>
              ) : (
                /* ASKED AND FOUND NOTHING. Only honest alongside the unexamined
                   count, which is why that number is repeated here: "nothing
                   references this column" and "nothing we could examine
                   references this column" are different sentences. */
                <div data-testid="no-references" className="px-4 py-8 text-center text-sm">
                  <div className="font-medium text-foreground/90">
                    {partialN > 0 && answer.unexaminedObjects === 0 ? (
                      <>
                        No reference found to <code className="font-mono">{scope}</code> in what
                        the runs could establish
                      </>
                    ) : (
                      <>
                        No object in scope references <code className="font-mono">{scope}</code>
                      </>
                    )}
                  </div>
                  <div className="mt-1 text-muted-foreground text-xs max-w-xl mx-auto">
                    {answer.unexaminedObjects > 0 ? (
                      <>
                        But {answer.unexaminedObjects.toLocaleString('en-IN')}{' '}
                        {answer.unexaminedObjects === 1 ? 'object' : 'objects'} could not be
                        examined, so this is not evidence that nothing touches the column - only
                        that nothing we could examine does.
                        {partialN > 0 && ' Some lineage runs also reported partial coverage.'}
                      </>
                    ) : partialN > 0 ? (
                      <span data-testid="no-references-partial">
                        But for {partialN.toLocaleString('en-IN')}{' '}
                        {partialN === 1 ? 'application' : 'applications'} the last lineage run
                        reported partial coverage, so this may be incomplete: a class there may use
                        this column without appearing here. This is not evidence that nothing
                        touches the column.
                      </span>
                    ) : (
                      <>
                        Every object in scope was examined and none of them reaches this column.
                      </>
                    )}
                  </div>
                </div>
              )
            ) : (
              answer.references.map((r, i) => (
                <motion.div
                  key={`${r.sourceEngine}:${r.sourceHost}:${r.sourcePort}:${r.sourceDatabase}:${r.objectSchema}.${r.objectName}:${r.columnName}:${r.direction}:${i}`}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.02, 0.3) }}
                >
                  <SqlObjectReferenceRow reference={r} partialRun={partialLocations.has(locationKey(r))} />
                </motion.div>
              ))
            )}
          </GlassCard>

          {pagerUseful && !filtersChanged && (
            <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
              <Button
                size="sm"
                variant="glass"
                disabled={loaded.offset === 0}
                onClick={() => void run(Math.max(0, loaded.offset - PAGE_SIZE), loaded.filters)}
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Previous
              </Button>
              <span>
                {pastTheEnd ? (
                  <>past the end of {answer.total.toLocaleString('en-IN')}</>
                ) : (
                  <>
                    Page {Math.floor(loaded.offset / PAGE_SIZE) + 1} of{' '}
                    {Math.max(1, Math.ceil(answer.total / PAGE_SIZE))}
                  </>
                )}
              </span>
              <Button
                size="sm"
                variant="glass"
                disabled={loaded.offset + answer.references.length >= answer.total}
                onClick={() => void run(loaded.offset + PAGE_SIZE, loaded.filters)}
              >
                Next <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
}
